// Generic onSnapshot hook for a single document with loading / error state.
//
// Resilient subscribe (activity-day hardening): mirrors
// useFirestoreCollection — on a terminal listener error we keep last-good
// data and re-subscribe with exponential backoff (1s→…→30s), except
// permission-denied which we surface and stop. Keeping `error` set on
// failure preserves useEventConfig's fail-open (error → learner let
// through, not locked out of the countdown gate).

import {
  type DocumentData,
  type DocumentReference,
  type FirestoreError,
  onSnapshot,
} from "firebase/firestore";
import { useEffect, useState } from "react";

export type WithId<T> = T & { id: string };

export interface DocState<T> {
  data: WithId<T> | null;
  loading: boolean;
  error: Error | null;
}

const MAX_BACKOFF_MS = 30_000;

export function useFirestoreDoc<T extends DocumentData>(
  ref: DocumentReference<T> | null,
): DocState<T> {
  const [state, setState] = useState<DocState<T>>({
    data: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!ref) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    let cancelled = false;
    let unsub: (() => void) | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;

    const subscribe = () => {
      if (cancelled) return;
      unsub = onSnapshot(
        ref,
        (snap) => {
          if (cancelled) return;
          attempt = 0; // recovered → reset backoff
          setState({
            data: snap.exists() ? { id: snap.id, ...snap.data() } : null,
            loading: false,
            error: null,
          });
        },
        (err) => {
          if (cancelled) return;
          // Keep last-good data so a weak-signal blip doesn't blank the UI.
          setState((prev) => ({ data: prev.data, loading: false, error: err }));
          unsub?.();
          unsub = null;
          if ((err as FirestoreError).code === "permission-denied") return;
          const delay = Math.min(1000 * 2 ** attempt, MAX_BACKOFF_MS);
          attempt += 1;
          retryTimer = setTimeout(subscribe, delay);
        },
      );
    };

    subscribe();

    return () => {
      cancelled = true;
      unsub?.();
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [ref]);

  return state;
}
